<template>
  <div class="soar-container flex flex-col gap-y-4">
    <h1 class="text-xl md:text-2xl text-primary font-medium border-b border-brand-gray-300">
      {{ $t('forgotUserName') }}
    </h1>
    <Form
      v-if="!isLoading"
      :initial-values="initialValues"
      ref="forgotForm"
      class="w-full flex flex-col gap-3"
      v-slot="{ values, resetField }"
      @submit="(values) => onSubmit(values, resetField)"
    >
      <div class="flex flex-col gap-y-4">
        <Field
          :rules="requiredRule"
          name="applicationsId"
          v-slot="{ field, errors: errs, handleChange }"
        >
          <Dropdown
            :field="field"
            :errors="errs"
            :label="$t('accountType')"
            :options="dropdownValues.applications"
            class="p-0 md:odd:ps-0 md:px-3 md:even:pe-0 w-full md:w-1/2"
            direction="flex-col"
            @on-selection="
              (item) => {
                handleChange(+item?.id)
              }
            "
          ></Dropdown>
        </Field>
        <div class="w-full flex-nowrap md:flex-row md:flex-wrap">
          <component
            :is="getForPerApplicationId(values.applicationsId)"
            :dropdowns="dropdownValues"
          ></component>
        </div>

        <PhoneSelector
          v-if="validateType === showMobileNumber"
          :registered-mobile-numbers="registeredMobileNumbers"
          class="pt-4 w-full border-t border-brand-gray-300"
        />

        <OtpAndChangeSelector
          v-if="validateType === check"
          class="pt-4 w-full flex flex-nowrap md:flex-row md:flex-wrap border-t border-brand-gray-300"
          @on-resend-otp="onSubmit(values)"
        />

        <template v-if="validateType === check && values.typeOfChange === 2">
          <div class="w-full flex gap-3">
            <Field
              :value="true"
              :unchecked-value="false"
              v-slot="{ field }"
              name="retrieveUsername"
              type="checkbox"
            >
              <label>
                <input
                  :name="field.name"
                  :value="true"
                  type="checkbox"
                  v-bind="field"
                  class="accent-primary"
                />
                {{ $t('Username') }}
              </label>
            </Field>
            <Field
              :value="true"
              :unchecked-value="false"
              v-slot="{ field }"
              name="retrievePassword"
              type="checkbox"
            >
              <label>
                <input
                  :name="field.name"
                  :value="true"
                  type="checkbox"
                  v-bind="field"
                  class="accent-primary"
                />
                {{ $t('Password') }}
              </label>
            </Field>
          </div>

          <div class="w-full flex-nowrap md:flex-row md:flex-wrap">
            <UsernameForm v-if="values.retrieveUsername && !values.retrievePassword" />
            <PasswordForm v-if="!values.retrieveUsername && values.retrievePassword" />
            <UsernameAndPasswordForm
              v-if="values.retrieveUsername && values.retrievePassword"
              :values
            />
          </div>
        </template>

        <div class="w-full flex flex-col items-start">
          <Field
            :rules="(value) => requiredRule(value, 'CAPTCHA_INVALID')"
            name="recaptchaResponse"
            v-slot="{ field, handleChange, errors }"
          >
            <GRecaptcha
              :key="recatpchaResetKey"
              ref="gRecaptchaRef"
              class="flex justify-center"
              @successCallback="(args) => handleChange(args)"
            ></GRecaptcha>
            <div class="flex flex-col gap-y-1 text-xs ltr:font-sans">
              <span :key="err" v-for="err in errors" class="text-brand-red">
                {{ $t(err) }}
              </span>
            </div>
          </Field>
        </div>
      </div>
      <div class="pt-4 w-full flex gap-x-4 border-t border-brand-gray-300">
        <button
          class="px-4 py-1 text-center rounded-md bg-brand-green/80 hover:bg-brand-green flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        >
          {{ $t('Send') }}
        </button>
        <RouterLink
          to="/"
          class="px-4 py-1 text-center rounded-md bg-brand-gray-600/80 hover:bg-brand-gray-600 flex items-center gap-x-2 hover:scale-[1.02] active:scale-100 disabled:bg-gray-300 disabled:hover:scale-95 disabled:cursor-not-allowed text-white"
        >
          {{ $t('Cancel') }}
        </RouterLink>
      </div>
    </Form>
  </div>
</template>

<script setup>
import client_lookups from '@/assets/json/clientLookups.json'
import GRecaptcha from '@/components/GRecaptcha/index.vue'
import Dropdown from '@/components/SimpleForm/Dropdown.vue'
import { useLookupsStore } from '@/stores/lookups'
import CitizenForm from '@/views/individuals/ForgotPassword/_CitizenForm.vue'
import ResidentForm from '@/views/individuals/ForgotPassword/_ResidentForm.vue'
import VisaholderForm from '@/views/individuals/ForgotPassword/_VisaholderForm.vue'
import { Field, Form } from 'vee-validate'
import { onMounted, reactive, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import OtpAndChangeSelector from '@/views/individuals/ForgotPassword/_OtpAndChangeSelector.vue'
import axiosInstance from '@/services'
import PhoneSelector from '@/views/individuals/ForgotPassword/_PhoneSelector.vue'
import UsernameForm from '@/views/individuals/ForgotPassword/_UsernameForm.vue'
import PasswordForm from '@/views/individuals/ForgotPassword/_PasswordForm.vue'
import UsernameAndPasswordForm from '@/views/individuals/ForgotPassword/_UsernameAndPasswordForm.vue'

const {
  application: { citizen, gcc_citizen, resident, dubaiResident, visaHolder },
  changeUsername: {
    validateType: { showMobileNumber, check }
  }
} = client_lookups

const allowedApplications = [citizen, gcc_citizen, resident, dubaiResident, visaHolder]

const isLoading = ref(true)
const initialValues = {}
// const initialValues = {
//   ...{
//     applicationsId: 5,
//     identityNumber: '784197701068645',
//     passportNumber: '06762681',
//     currentNationalityID: 8,
//     dateOfBirth: '1994-08-26'
//   }
// }

const i18n = useI18n()
const lookupsStore = useLookupsStore()

const registeredMobileNumbers = ref()
const forgotForm = ref()
const recatpchaResetKey = ref()
const validateType = ref()

const dropdownValues = reactive({
  applications: [],
  countries: [],
  passportTypes: []
})

onMounted(async () => {
  await fetchLookups()
  isLoading.value = false
})

async function fetchLookups() {
  const applications = await lookupsStore.fetchLookup('/api/lookup/applications', {}, {}, true)
  dropdownValues.applications = applications
    .filter((a) => allowedApplications.includes(a.id))
    .map((a) => {
      return {
        ...a,
        label: a.localizedName,
        code: a.id,
        value: a.id
      }
    })

  dropdownValues.countries = await lookupsStore.fetchLookup('/api/lookup/countries')
  dropdownValues.passportTypes = await lookupsStore.fetchLookup('/api/lookup/passportTypes')
}

function getForPerApplicationId(applicationId) {
  switch (applicationId) {
    case citizen:
      return CitizenForm

    case gcc_citizen:
    case dubaiResident:
    case resident:
      return ResidentForm

    case visaHolder:
      return VisaholderForm

    default:
      break
  }
}

function requiredRule(value, errorMessage = 'INVALID_REQUIRED') {
  if (!value || !`${value}`.trim()) {
    return i18n.t(errorMessage)
  }

  return true
}

async function onSubmit(form) {
  // set typeOfChange = 3 when registerdMobileNumberId exists in form
  if ('registerdMobileNumberId' in form) {
    Object.assign(form, { typeOfChange: 3 })
  }

  const response = await axiosInstance.post('/api/user/forgotUserName', form)
  const { registeredMobileNumber } = response

  if (registeredMobileNumber) {
    registeredMobileNumbers.value = registeredMobileNumber
  }

  validateType.value = response.validateType
  recatpchaResetKey.value = Math.random()
  forgotForm.value.resetField('recaptchaResponse')
}
</script>

<style lang="scss" scoped></style>
